import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import ProductCard from '../components/ProductCard';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ArrowRight, Sparkles, Droplets, Shield, Heart, CheckCircle, Truck, BookOpen, Star, Package } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const benefits = [
  { icon: CheckCircle, title: "Productos Autenticos", desc: "Importados directamente de Corea del Sur con garantia de autenticidad." },
  { icon: Star, title: "Seleccion Curada", desc: "Cada producto es seleccionado por nuestros expertos en skincare coreano." },
  { icon: BookOpen, title: "Educacion Skincare", desc: "Te ensenamos sobre K-beauty: rutinas, ingredientes y guias para tu piel." },
  { icon: Truck, title: "Envios en Puerto Rico", desc: "Entrega rapida y segura en todo Puerto Rico." },
];

const categoryShowcase = [
  { key: 'serums', label: 'Serums', desc: 'Tratamientos concentrados' },
  { key: 'protectores_solares', label: 'Protectores Solares', desc: 'Proteccion diaria' },
  { key: 'toners', label: 'Toners', desc: 'Equilibra tu piel' },
  { key: 'limpiadores', label: 'Limpiadores', desc: 'Limpieza profunda' },
  { key: 'kits', label: 'Kits', desc: 'Rutinas completas' },
];

export default function HomePage() {
  const [featured, setFeatured] = useState([]);
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    axios.get(`${API}/products?featured=true`).then(r => setFeatured(r.data.slice(0, 8))).catch(() => {});
    axios.get(`${API}/settings`).then(r => setSettings(r.data)).catch(() => {});
  }, []);

  const heroTitle = settings?.hero_title || 'Descubre el secreto del skincare coreano';
  const heroSubtitle = settings?.hero_subtitle || 'Productos autenticos de K-Beauty seleccionados para ayudarte a lograr una piel saludable y luminosa.';
  const bannerText = settings?.banner_text || 'K-Beauty Autentico desde Corea del Sur';
  const heroImage = settings?.hero_image || 'https://customer-assets.emergentagent.com/job_aura-k-beauty/artifacts/em1zalck_IMG_0259.WEBP';

  // Apply dynamic colors from settings
  useEffect(() => {
    if (settings?.colors) {
      const root = document.documentElement;
      const c = settings.colors;
      if (c.violet) root.style.setProperty('--violet', c.violet);
      if (c.violet_dark) root.style.setProperty('--violet-dark', c.violet_dark);
      if (c.violet_light) root.style.setProperty('--violet-light', c.violet_light);
      if (c.sage) root.style.setProperty('--sage', c.sage);
      if (c.sage_dark) root.style.setProperty('--sage-dark', c.sage_dark);
      if (c.sage_light) root.style.setProperty('--sage-light', c.sage_light);
      if (c.dark) root.style.setProperty('--dark', c.dark);
      if (c.cream) root.style.setProperty('--cream', c.cream);
    }
  }, [settings]);

  return (
    <div data-testid="home-page">
      {/* Hero */}
      <section className="hero-pattern relative min-h-[80vh] flex items-center" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <div className="space-y-7 slide-up">
              <div className="inline-flex items-center gap-2 bg-[var(--sage-light)] text-[var(--sage-dark)] px-4 py-1.5 rounded-full text-xs font-semibold tracking-wide">
                <Sparkles className="h-3.5 w-3.5" />
                {bannerText}
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[var(--dark)] leading-[1.1]">
                {heroTitle.includes('secreto') ? (
                  <>Descubre el <span className="gradient-text">secreto</span> del skincare coreano</>
                ) : heroTitle}
              </h1>
              <p className="text-sm lg:text-base text-[var(--dark)]/50 max-w-md leading-relaxed">
                {heroSubtitle}
              </p>
              <div className="flex flex-wrap gap-3">
                <Link to="/tienda">
                  <Button size="lg" className="bg-[var(--dark)] hover:bg-[var(--dark)]/90 text-white rounded-xl px-8 h-12 text-sm font-semibold tracking-wide" data-testid="hero-shop-btn">
                    Comprar ahora <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
                <Link to="/educacion">
                  <Button size="lg" variant="outline" className="border-[var(--dark)]/15 text-[var(--dark)]/70 hover:bg-[var(--cream)] rounded-xl px-8 h-12 text-sm" data-testid="hero-learn-btn">
                    Aprender sobre K-Beauty
                  </Button>
                </Link>
              </div>
            </div>
            <div className="relative slide-up stagger-2 hidden lg:block">
              <div className="absolute -inset-4 bg-[var(--sage-light)] rounded-[2.5rem] rotate-2"></div>
              <img
                src={heroImage}
                alt="Korean Skincare Products"
                className="relative rounded-[2rem] w-full h-[480px] object-cover shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 bg-white" data-testid="benefits-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="text-3xl sm:text-4xl font-bold text-[var(--dark)] mb-3">Por que comprar en Aura K Beauty?</h2>
            <p className="text-base text-[var(--dark)]/50 max-w-lg mx-auto">Tu tienda confiable de skincare coreano en Puerto Rico.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((b, i) => (
              <div key={i} className={`slide-up stagger-${i + 1} text-center p-6 rounded-2xl border border-[var(--sage)]/20 hover:border-[var(--violet)]/30 transition-colors`}>
                <div className="w-14 h-14 mx-auto mb-4 rounded-2xl bg-[var(--sage-light)] flex items-center justify-center">
                  <b.icon className="h-6 w-6 text-[var(--sage-dark)]" />
                </div>
                <h3 className="text-base font-bold text-[var(--dark)] mb-2">{b.title}</h3>
                <p className="text-sm text-[var(--dark)]/50 leading-relaxed">{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Category Showcase */}
      <section className="py-14 bg-white" data-testid="categories-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-2xl sm:text-3xl font-bold text-[var(--dark)] mb-2">Explora por Categoria</h2>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
            {categoryShowcase.map(cat => (
              <Link key={cat.key} to={`/tienda?category=${cat.key}`}>
                <div className="group text-center p-5 rounded-2xl border border-[var(--sage)]/15 hover:border-[var(--violet)]/30 hover:shadow-md transition-all bg-[var(--cream)]/50 cursor-pointer">
                  <div className="w-12 h-12 mx-auto mb-3 rounded-xl bg-[var(--sage-light)] flex items-center justify-center group-hover:bg-[var(--violet-light)] transition-colors">
                    <Package className="h-5 w-5 text-[var(--sage-dark)] group-hover:text-[var(--violet)] transition-colors" />
                  </div>
                  <span className="text-sm font-semibold text-[var(--dark)] block">{cat.label}</span>
                  <span className="text-[10px] text-[var(--dark)]/35 mt-0.5 block">{cat.desc}</span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Products */}
      <section className="py-16 bg-[var(--cream)]" data-testid="featured-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between mb-8">
            <div>
              <Badge className="bg-[var(--violet-light)] text-[var(--violet)] border-0 text-[10px] font-semibold mb-2">Best Sellers</Badge>
              <h2 className="text-2xl sm:text-3xl font-bold text-[var(--dark)]">Productos Destacados</h2>
            </div>
            <Link to="/tienda" className="hidden sm:block">
              <Button variant="ghost" className="text-[var(--violet)] hover:text-[var(--violet-dark)] text-sm">
                Ver todos <ArrowRight className="ml-1 h-4 w-4" />
              </Button>
            </Link>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-5">
            {featured.map(p => <ProductCard key={p.id} product={p} />)}
          </div>
          <div className="sm:hidden text-center mt-8">
            <Link to="/tienda">
              <Button className="bg-[var(--violet)] text-white rounded-full px-8 text-sm">Ver toda la tienda</Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Aura Skin Lab Teaser */}
      <section className="py-20 bg-white" data-testid="skinlab-teaser">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <Badge className="bg-[var(--violet-light)] text-[var(--violet)] border-0 mb-4 text-xs">Aura Skin Lab</Badge>
              <h2 className="text-3xl sm:text-4xl font-bold text-[var(--dark)] mb-4">Aprende sobre K-Beauty</h2>
              <p className="text-base text-[var(--dark)]/60 mb-6 leading-relaxed">
                Descubre los secretos del skincare coreano. Desde la famosa rutina de 10 pasos hasta como lograr el glass skin, te ensenamos todo lo que necesitas saber.
              </p>
              <div className="space-y-3 mb-8">
                {['Rutina coreana de skincare paso a paso', 'Que es el glass skin y como lograrlo', 'Ingredientes coreanos: centella, ginseng, mucina de caracol', 'Guias para cada tipo de piel'].map((item, i) => (
                  <div key={i} className="flex items-center gap-3 text-sm text-[var(--dark)]/60">
                    <CheckCircle className="h-4 w-4 text-[var(--sage-dark)] flex-shrink-0" />
                    {item}
                  </div>
                ))}
              </div>
              <Link to="/educacion">
                <Button className="bg-[var(--sage)] hover:bg-[var(--sage-dark)] text-[var(--dark)] rounded-full px-8 h-11 font-semibold" data-testid="skinlab-btn">
                  Explorar Aura Skin Lab <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>
            <div className="relative">
              <img
                src="https://customer-assets.emergentagent.com/job_aura-k-beauty/artifacts/hdw6z399_IMG_0256.WEBP"
                alt="K-Beauty Education"
                className="rounded-3xl w-full h-[400px] object-cover shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Skin Quiz CTA */}
      <section className="py-16 bg-[var(--cream)]" data-testid="quiz-cta-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-r from-[var(--violet-light)] to-white rounded-3xl p-8 sm:p-10 border border-[var(--violet)]/10 flex flex-col sm:flex-row items-center gap-6">
            <div className="w-16 h-16 rounded-2xl bg-[var(--violet)] flex items-center justify-center flex-shrink-0">
              <Sparkles className="h-8 w-8 text-white" />
            </div>
            <div className="flex-1 text-center sm:text-left">
              <h3 className="text-xl font-bold text-[var(--dark)] mb-1">No sabes por donde empezar?</h3>
              <p className="text-sm text-[var(--dark)]/50">Toma nuestro quiz de 5 preguntas y descubre los productos perfectos para tu tipo de piel.</p>
            </div>
            <Link to="/quiz">
              <Button size="lg" className="bg-[var(--violet)] hover:bg-[var(--violet-dark)] text-white rounded-xl px-8 h-12 text-sm font-semibold whitespace-nowrap" data-testid="quiz-cta-btn">
                Hacer el Skin Quiz <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[var(--violet)] text-white" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-4">Comienza tu Rutina K-Beauty Hoy</h2>
          <p className="text-lg text-white/70 mb-8 max-w-2xl mx-auto">
            No importa tu tipo de piel, tenemos la rutina perfecta para ti. Descubre los kits recomendados por nuestros expertos.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/kits">
              <Button size="lg" className="bg-white text-[var(--violet)] hover:bg-[var(--sage)] rounded-full px-10 h-12 text-base font-semibold" data-testid="cta-kits-btn">
                Ver Kits y Rutinas
              </Button>
            </Link>
            <a href="https://wa.me/17879491006?text=Hola%20Aura%20K%20Beauty!%20Necesito%20ayuda%20para%20elegir%20mi%20rutina." target="_blank" rel="noopener noreferrer">
              <Button size="lg" variant="outline" className="border-white/40 text-white hover:bg-white/10 rounded-full px-10 h-12 text-base">
                Asesoria por WhatsApp
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
